/* Adapted from Sticky Grid Scroll by Theo Plawinski / Codrops (MIT).
   Source: 6015d6c7e66d371618c82b5fb0126d278f88e42d.
   Full notice: THIRD_PARTY_NOTICES-v17.txt. Native scroll; existing theme GSAP. */
(() => {
  'use strict';
  if (window.ITPStickyGridV17) return;
  const instances = new Map();
  const html = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const allowed = () => html.dataset.readingCalm !== 'on' && document.body.dataset.cinema15Enabled !== 'false' && (html.dataset.motion === 'on' || (!html.dataset.motion && !reduced.matches));
  const overviewVisible = root => !root.hasAttribute('data-sg17-overview-only') || !new URL(location.href).searchParams.get('category');

  function mount(root) {
    if (instances.has(root) || !allowed() || root.dataset.sg17Enabled === 'false' || !overviewVisible(root) || !window.gsap?.context || !window.ScrollTrigger?.create) return;
    const { gsap, ScrollTrigger } = window;
    const block = root.querySelector('[data-sg17-block]');
    const wrapper = root.querySelector('[data-sg17-wrapper]');
    const content = root.querySelector('[data-sg17-content]');
    const title = root.querySelector('[data-sg17-title]');
    const description = root.querySelector('[data-sg17-description]');
    const button = root.querySelector('[data-sg17-button]');
    const grid = root.querySelector('[data-sg17-grid]');
    const items = [...root.querySelectorAll('[data-sg17-item]')];
    if (!block || !wrapper || !content || !title || !description || !button || !grid || items.length < 3) return;
    const controller = new AbortController();
    const originalLoading = new Map();
    let context, timeline, titleOffsetY = 0, resizeTimer = 0, preloadTimer = 0, disposed = false, active = false;

    const dispose = (keepReady = false) => {
      if (disposed) return;
      disposed = true;
      clearTimeout(resizeTimer); clearTimeout(preloadTimer); controller.abort();
      context?.revert();
      originalLoading.forEach((value, image) => value === null ? image.removeAttribute('loading') : image.setAttribute('loading', value));
      if (!keepReady) delete root.dataset.sg17Ready;
      delete root.dataset.sg17Progress; delete root.dataset.sg17Phase;
      root.dataset.sg17State = 'static';
      instances.delete(root);
    };
    instances.set(root, dispose);

    const toggleContent = isVisible => {
      if (disposed || !active) return;
      context.add(() => {
        gsap.timeline({ defaults: { overwrite: true } })
          .to(title, { yPercent: isVisible ? 0 : titleOffsetY, duration: 0.7, ease: 'power2.inOut' })
          .to([description, button], { opacity: isVisible ? 1 : 0, duration: 0.4, ease: `power1.${isVisible ? 'inOut' : 'out'}`, pointerEvents: isVisible ? 'auto' : 'none' }, isVisible ? '-=90%' : '<');
      });
    };

    const activate = () => {
      if (disposed || !root.isConnected || !allowed() || active) return;
      clearTimeout(preloadTimer);
      if ([...grid.querySelectorAll('img')].some(image => image.complete && !image.naturalWidth)) {
        delete root.dataset.sg17Ready;
        root.dataset.sg17State = 'static-image-error';
        return;
      }
      try {
      gsap.registerPlugin(ScrollTrigger);
      active = true;
      root.dataset.sg17Ready = 'true';
      root.dataset.sg17State = 'active';
      context = gsap.context(() => {}, root);
      context.add(() => {
        gsap.set([description, button], { opacity: 0, pointerEvents: 'none' });
        const dy = (content.offsetHeight - title.offsetHeight) / 2;
        titleOffsetY = (dy / content.offsetHeight) * 100;
        gsap.set(title, { yPercent: titleOffsetY });

        // Original wrapper parallax and title entrance.
        gsap.from(wrapper, { yPercent: -100, ease: 'none', scrollTrigger: { id: `sg17:parallax:${root.id}`, trigger: block, start: 'top bottom', end: 'top top', scrub: true, invalidateOnRefresh: true } });
        gsap.from(title, { opacity: 0, duration: 0.7, ease: 'power1.out', scrollTrigger: { id: `sg17:title:${root.id}`, trigger: block, start: 'top 57%', toggleActions: 'play none none reset' } });

        const columns = Array.from({ length: 3 }, () => []);
        items.forEach((item, index) => columns[index % 3].push(item));

        // Original alternating columns: outer columns descend, middle ascends.
        const reveal = gsap.timeline();
        const distance = window.innerHeight - (window.innerHeight - grid.offsetHeight) / 2;
        columns.forEach((column, colIndex) => {
          const fromTop = colIndex % 2 === 0;
          reveal.from(column, { y: distance * (fromTop ? -1 : 1), stagger: { each: 0.06, from: fromTop ? 'end' : 'start' }, ease: 'power1.inOut' }, 'grid-reveal');
        });

        // Original 2.05 zoom, lateral ±40%, center column vertical split.
        const zoom = gsap.timeline({ defaults: { duration: 1, ease: 'power3.inOut' } });
        zoom.to(grid, { scale: 2.05 });
        zoom.to(columns[0], { xPercent: -40 }, '<');
        zoom.to(columns[2], { xPercent: 40 }, '<');
        zoom.to(columns[1], { yPercent: index => (index < Math.floor(columns[1].length / 2) ? -1 : 1) * 40, duration: 0.5, ease: 'power1.inOut' }, '-=0.5');

        timeline = gsap.timeline({ scrollTrigger: { id: `sg17:grid:${root.id}`, trigger: block, start: 'top 25%', end: 'bottom bottom', scrub: true, invalidateOnRefresh: true, onUpdate: self => {
          root.dataset.sg17Progress = self.progress.toFixed(4);
          root.dataset.sg17Phase = self.progress < 0.074 ? 'columns' : self.progress < 0.63 ? 'columns-and-zoom' : self.progress < 0.704 ? 'zoom' : 'content';
        } } });
        timeline.add(reveal).add(zoom, '-=0.6').add(() => toggleContent(timeline.scrollTrigger.direction === 1), '-=0.32');
      });
      ScrollTrigger.refresh();
      // A browser restoring a deep scroll position may start after the callback.
      if (timeline.scrollTrigger.progress >= 0.76 / timeline.duration()) toggleContent(true);
      } catch {
        dispose();
        root.dataset.sg17State = 'static-unavailable';
      }
    };

    const images = [...grid.querySelectorAll('img')];
    const loaded = images.map(image => {
      originalLoading.set(image, image.getAttribute('loading')); image.loading = 'eager';
      if (image.complete) return Promise.resolve();
      return new Promise(resolve => {
        image.addEventListener('load', resolve, { once: true, signal: controller.signal });
        image.addEventListener('error', resolve, { once: true, signal: controller.signal });
      });
    });
    root.dataset.sg17State = 'loading';
    Promise.race([Promise.all(loaded), new Promise(resolve => { preloadTimer = setTimeout(resolve, 5000); })]).then(activate);
    grid.addEventListener('load', () => { if (active) ScrollTrigger.refresh(); }, { capture: true, signal: controller.signal });
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { if (disposed) return; dispose(true); mount(root); }, 250);
    }, { passive: true, signal: controller.signal });
    content.addEventListener('focusin', event => {
      if (!button.contains(event.target) || !timeline?.scrollTrigger) return;
      const trigger = timeline.scrollTrigger;
      window.scrollTo({ top: trigger.start + (trigger.end - trigger.start) * 0.97, behavior: 'instant' });
      toggleContent(true);
    }, { signal: controller.signal });
    document.fonts?.ready.then(() => { if (!disposed && active) ScrollTrigger.refresh(); });
  }

  function scan(scope = document) {
    const roots = [...(scope.matches?.('[data-sg17]') ? [scope] : []), ...(scope.querySelectorAll?.('[data-sg17]') || [])];
    roots.forEach(root => {
      root.hidden = !overviewVisible(root);
      if (!root.hidden) mount(root);
    });
  }
  const refresh = () => {
    [...instances.keys()].forEach(root => { if (!root.isConnected || !allowed() || !overviewVisible(root)) instances.get(root)?.(); });
    scan();
  };
  new MutationObserver(refresh).observe(html, { attributes: true, attributeFilter: ['data-motion', 'data-motion-choice', 'data-reading-calm'] });
  new MutationObserver(refresh).observe(document.body, { attributes: true, attributeFilter: ['data-cinema15-enabled'] });
  reduced.addEventListener('change', refresh);
  window.addEventListener('popstate', refresh);
  // The native catalog uses pushState for selection/reset, without popstate.
  // Observe its completed native event; do not intercept links or controls.
  const catalogChanged = event => {
    if (event.target.closest?.('[data-itp-catalog]')) queueMicrotask(refresh);
  };
  document.addEventListener('click', catalogChanged);
  document.addEventListener('change', catalogChanged);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => [...instances.keys()].filter(root => root === event.target || event.target.contains(root)).forEach(root => instances.get(root)?.()));
  document.addEventListener('shopify:block:select', event => {
    const tile = event.target.closest?.('[data-sg17-item]');
    const root = tile?.closest('[data-sg17]');
    if (!root || !allowed() || root.dataset.sg17Ready !== 'true') return;
    const block = root.querySelector('[data-sg17-block]');
    const start = block.getBoundingClientRect().top + scrollY - innerHeight * 0.25;
    const travel = block.offsetHeight - innerHeight * 0.75;
    window.scrollTo({ top: start + travel * 0.42, behavior: 'instant' });
  });
  window.ITPStickyGridV17 = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true }); else refresh();
})();
